import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faClose, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { AnimatePresence } from 'framer-motion';

import { sendChatMessage, IChatMessage, AIProvider } from '../../apis/aiService';
import { getAllMediaContextFromData } from './mediaContext';
import { useContentData } from '../../hooks/useContentData';
import * as S from './style';

const QUICK_ACTIONS = [
  { label: 'APEC 영상 추천', message: 'APEC 관련 영상을 추천해주세요' },
  { label: '콘텐츠 검색', message: '어떤 콘텐츠가 있나요?' },
  { label: '서라벌 소개', message: '서라벌 천년왕경 콘텐츠에 대해 알려주세요' },
  { label: '사용법 안내', message: 'GDFLIX 사용 방법을 알려주세요' },
];

function AIChatbot() {
  const { data: contentData } = useContentData();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<IChatMessage[]>([
    { role: 'assistant', content: '안녕하세요! GDFLIX AI 어시스턴트입니다.\n콘텐츠 추천, 검색, 플랫폼 안내 등 무엇이든 물어보세요!' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState<AIProvider>('ollama');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage: IChatMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Build context from available media
      const context = contentData ? getAllMediaContextFromData(contentData) : '';

      const reply = await sendChatMessage(
        newMessages.filter(m => m.role === 'user' || m.role === 'assistant'),
        provider,
        context
      );

      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `죄송합니다, 오류가 발생했습니다: ${errorMsg}\n\n다른 AI 모델로 전환해보시거나 다시 시도해주세요.`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const handleQuickAction = (message: string) => {
    handleSend(message);
  };

  return (
    <>
      {/* Floating Action Button */}
      <S.FAB
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="AI 챗봇 열기"
      >
        <FontAwesomeIcon icon={isOpen ? faClose : faRobot} />
      </S.FAB>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <S.ChatWindow
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <S.ChatHeader>
              <S.ChatHeaderTitle>
                <span className="ai-icon"><FontAwesomeIcon icon={faRobot} /></span>
                GDFLIX AI
              </S.ChatHeaderTitle>
              <S.ProviderSelect
                value={provider}
                onChange={(e) => setProvider(e.target.value as AIProvider)}
              >
                <option value="ollama">Ollama (Local)</option>
                <option value="anthropic">Claude AI</option>
                <option value="perplexity">Perplexity</option>
              </S.ProviderSelect>
              <S.CloseBtn onClick={() => setIsOpen(false)}>
                <FontAwesomeIcon icon={faClose} />
              </S.CloseBtn>
            </S.ChatHeader>

            {/* Messages */}
            <S.MessagesArea>
              {messages.map((msg, i) => (
                <S.MessageBubble key={i} $isUser={msg.role === 'user'}>
                  {msg.content}
                </S.MessageBubble>
              ))}
              {isLoading && (
                <S.TypingIndicator>
                  <span /><span /><span />
                </S.TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </S.MessagesArea>

            {/* Quick Actions */}
            {messages.length <= 2 && (
              <S.QuickActions>
                {QUICK_ACTIONS.map((action, i) => (
                  <S.QuickActionBtn
                    key={i}
                    onClick={() => handleQuickAction(action.message)}
                    disabled={isLoading}
                  >
                    {action.label}
                  </S.QuickActionBtn>
                ))}
              </S.QuickActions>
            )}

            {/* Input */}
            <S.InputArea onSubmit={handleSubmit}>
              <S.ChatInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="메시지를 입력하세요..."
                disabled={isLoading}
                autoFocus
              />
              <S.SendButton type="submit" disabled={!input.trim() || isLoading}>
                <FontAwesomeIcon icon={faPaperPlane} />
              </S.SendButton>
            </S.InputArea>
          </S.ChatWindow>
        )}
      </AnimatePresence>
    </>
  );
}

export default AIChatbot;
